import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ExternalLink } from 'lucide-react';

const Popup = ({ popup, onClose }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Show popup with animation after mount
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(onClose, 300); // Wait for animation to complete
  };

  const handleCTAClick = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
    handleClose();
  };

  const backgroundStyle = popup.backgroundType === 'image'
    ? { backgroundImage: `url(${popup.backgroundImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }
    : { backgroundColor: popup.backgroundColor };

  return (
    <AnimatePresence>
      {isVisible && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
            onClick={popup.closable ? handleClose : undefined}
          />

          {/* Popup */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="fixed inset-0 flex items-center justify-center z-50 p-4"
          >
            <div
              className="relative max-w-md w-full mx-auto rounded-2xl overflow-hidden shadow-2xl"
              style={backgroundStyle}
            >
              {/* Glassmorphism overlay */}
              <div className="absolute inset-0 bg-white/10 backdrop-blur-md border border-white/20" />

              {/* Content */}
              <div className="relative p-6 text-white">
                {/* Close button */}
                {popup.closable && (
                  <button
                    onClick={handleClose}
                    className="absolute top-4 right-4 p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors backdrop-blur-sm"
                  >
                    <X size={20} />
                  </button>
                )}

                {/* Title */}
                <h2 className="text-2xl font-bold mb-3 leading-tight">
                  {popup.title}
                </h2>

                {/* Description */}
                <p className="text-white/90 mb-6 leading-relaxed">
                  {popup.description}
                </p>

                {/* CTAs */}
                {popup.ctas && popup.ctas.length > 0 && (
                  <div className="flex flex-col sm:flex-row gap-3">
                    {popup.ctas.map((cta, index) => (
                      <button
                        key={index}
                        onClick={() => handleCTAClick(cta.url)}
                        className={`flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all duration-200 ${
                          cta.primary
                            ? 'bg-white text-gray-900 hover:bg-gray-100 shadow-lg'
                            : 'bg-white/20 hover:bg-white/30 backdrop-blur-sm border border-white/30'
                        }`}
                      >
                        {cta.text}
                        <ExternalLink size={16} />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Bottom accent */}
              <div className="h-1 bg-gradient-to-r from-transparent via-white/50 to-transparent" />
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default Popup;
